import { Upload } from '@mui/icons-material'
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Theme,
	Typography,
} from '@mui/material'
import { makeStyles } from '@mui/styles'
import clsx from 'clsx'
import { percent } from 'csx'
import { useCallback, useState } from 'react'
import Dropzone, { DropzoneState } from 'react-dropzone'
import { v4 as uuidv4 } from 'uuid'
import { SupportingDocumentIndex } from '../../../api/DTOtemp'
import { WriteWorkflowTaskModel } from '../../../api/clients/workflowTasks/DTOs'
import { ResourceType } from '../../../pages/PackageHostPage/Attachments/AttachFilesDialog'
import { toastService } from '../../../services/notifications/ToastService'
import { SessionService } from '../../../services/session'
import { uploadBlob } from '../../../utils/BlobUtils'
import {
	IInteractiveWorkflowTask,
	IInteractiveWorkflowTaskProps,
	InteractiveWorkflowTaskState,
} from './IInteractiveWorkflowTask'

type RequestFileTask =
	InteractiveWorkflowTaskState<RequestFileTaskInteractiveData>

type RequestFileTaskInteractiveData = {
	message: string
	acceptedFileTypes: string[]
	supportingDocuments: SupportingDocumentIndex[]
	attachmentType: string
}

export const RequestFileInteractiveTaskId = 'RequestFile'

const RequestFileInteraction = (
	props: IInteractiveWorkflowTaskProps<WriteWorkflowTaskModel<RequestFileTask>>,
) => {
	const [file, setFile] = useState<File | undefined>()
	const [isUploading, setIsUploading] = useState(false)
	const signalTypeId = 'FileUploaded'

	const sessionService = SessionService.getInstance()

	const styles = useStyles()

	const fileReader = new FileReader()

	const handleDrop = useCallback((acceptedFiles: File[]) => {
		const newFile = acceptedFiles[0]

		try {
			fileReader.readAsArrayBuffer(newFile as Blob)
		} catch (e) {
			console.log(e)

			throw new Error('error reading file as array buffer')
		}

		setFile(newFile)
	}, [])

	const handleCancel = () =>
		props.onCancel({
			result: 'cancel',
			signalTypeId: signalTypeId,
			resources: [
				{
					type: 'FormPackage',
					value: props.task.workflowTaskState.resourceId,
				},
				{
					type: 'WorkflowTask',
					value: {
						id: props.task.id,
						lockPassword: props.task.lockPassword,
						outcome: 'Cancel',
						results: {},
						userId: sessionService.userId,
					},
				},
			],
		})

	const handleUpload = async () => {
		if (file === undefined) return

		setIsUploading(true)

		// first upload the blob (in chunks if necessary)
		const blobId = await uploadBlob(fileReader, file).finally(() =>
			setIsUploading(false),
		)

		if (blobId === undefined) throw new Error('error uploading blob')

		props.onConfirm({
			result: 'okay',
			signalTypeId: signalTypeId,
			resources: [
				{
					type: 'FormPackage',
					value: props.task.workflowTaskState.resourceId,
				},
				{
					type: 'WorkflowTask',
					value: {
						id: props.task.id,
						lockPassword: props.task.lockPassword,
						outcome: 'Upload',
						results: {
							type: ResourceType.Attachment,
							contentType: file.type,
							name: file.name,
							resourceId: uuidv4(),
							packageAttachmentType:
								props.task.workflowTaskState.interactionData.attachmentType,
						} as SupportingDocumentIndex,
						userId: sessionService.userId,
					},
				},
				{
					type: 'Blob',
					value: blobId,
				},
			],
		})
	}

	return (
		<Box component={Dialog} open={true} fullWidth>
			<DialogTitle>Upload File</DialogTitle>
			<Box
				component={DialogContent}
				display="flex"
				flexDirection="column"
				gap={2}
			>
				{props.task.workflowTaskState.interactionData.message}
				<Dropzone
					onDrop={(files) => handleDrop(files)}
					accept={props.task.workflowTaskState.interactionData.acceptedFileTypes.join(
						',',
					)}
					maxFiles={1}
					maxSize={30000000}
					multiple={false}
					onDropRejected={(rejectedFiles) => {
						for (const fileRejection of rejectedFiles) {
							console.error(
								`file ${fileRejection.file.name} rejected:`,
								fileRejection,
							)
							for (const error of fileRejection.errors) {
								if (error.code === 'file-too-large')
									toastService.displayToast({
										message: 'File exceeds max size of 30MB',
										area: 'global',
										severity: 'error',
									})
								else {
									toastService.displayToast({
										message: error.message,
										area: 'global',
										severity: 'error',
									})
								}
							}
						}
					}}
				>
					{({ acceptedFiles, getRootProps, getInputProps }: DropzoneState) => (
						<Box
							component="section"
							flex={1}
							className={clsx(
								styles.verticalSpacing,
								styles.enabledDragAndDrop,
							)}
						>
							<Box height={percent(100)} {...getRootProps()}>
								<input {...getInputProps()} />
								<div className={styles.dragAndDropBody}>
									<Box
										display="flex"
										flexDirection="column"
										justifyContent="center"
										alignItems="center"
										height={percent(100)}
									>
										<Box
											overflow="auto"
											width={percent(100)}
											textAlign="center"
										>
											<Box
												flex="1"
												overflow="hidden"
												display="flex"
												flexDirection="column"
												justifyContent="center"
												alignItems="center"
											>
												<Upload />
												{acceptedFiles.length == 0 ? (
													<Typography>
														Drag and drop a file here, or click to select a file
													</Typography>
												) : (
													acceptedFiles.map((v) => (
														<Typography key={v.name}>{v.name}</Typography>
													))
												)}
											</Box>
										</Box>
									</Box>
								</div>
							</Box>
						</Box>
					)}
				</Dropzone>
			</Box>

			<DialogActions>
				<Button variant="outlined" onClick={handleCancel}>
					Cancel
				</Button>
				<Button
					variant="contained"
					disabled={file === undefined || isUploading}
					onClick={handleUpload}
				>
					Upload
				</Button>
			</DialogActions>
		</Box>
	)
}

export const RequestFileInteractiveTaskDefinition: IInteractiveWorkflowTask<
	WriteWorkflowTaskModel<RequestFileTask>
> = {
	id: RequestFileInteractiveTaskId,
	interaction: RequestFileInteraction,
}

const useStyles = makeStyles((theme: Theme) => ({
	verticalSpacing: {
		height: '50%',
		flex: 3,
	},
	enabledDragAndDrop: {
		cursor: 'pointer',
	},

	disabledDragAndDrop: {
		opacity: percent(60),
	},

	dragAndDropBody: {
		height: percent(100),
		width: percent(100),
		display: 'flex',
		flexDirection: 'column',
		overflow: 'hidden',

		justifyContent: 'center',
		alignItems: 'center',

		borderRadius: theme.shape.borderRadius,
		borderWidth: 2,
		borderStyle: 'dashed',
		borderColor: theme.palette.text.secondary,

		overflowWrap: 'break-word',

		padding: theme.spacing(2),

		[theme.breakpoints.up('sm')]: {
			padding: theme.spacing(5),
		},
	},
}))
